/**
 * Obľúbené produkty – klientská vrstva.
 *
 * Hosť      → localStorage, viazané na zariadenie
 * Prihlásený → zákaznícky metafield cez app proxy /apps/wishlist
 *
 * Počiatočný stav prihláseného sa nečíta cez fetch, ale vykreslí ho Liquid do
 * DexWishlistConfig. Vďaka tomu majú srdiečka správny stav hneď pri načítaní
 * stránky a neblikajú.
 *
 * Pracuje sa výhradne s ID VARIANTU – klient to vyžiadal kvôli funkcii
 * „opäť na sklade", ktorá sa dá sledovať len na variante.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'dex_wishlist';
  var cfg = window.DexWishlistConfig || {};
  var PROXY = cfg.proxy || '/apps/wishlist';
  var loggedIn = !!cfg.loggedIn;

  /** Metafield vracia GID, localStorage aj DOM číselné ID. Zjednotíme na číslo. */
  function toId(value) {
    var m = String(value == null ? '' : value).match(/(\d+)\s*$/);
    return m ? m[1] : null;
  }

  /**
   * Mapa ID variantu → handle produktu. Panel potrebuje dotiahnuť fotku a cenu
   * a zo samotného ID variantu sa produkt na storefronte dohľadať nedá.
   * Prihlásenému ju napĺňa Liquid a endpoint /list?detailed=1, hosťovi
   * localStorage pri každom kliknutí na srdiečko.
   */
  var handles = {};
  (cfg.items || []).forEach(function (entry) {
    var id = toId(entry && entry.id != null ? entry.id : entry);
    if (id && entry && entry.handle) handles[id] = entry.handle;
  });

  function readLocalEntries() {
    try {
      var raw = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (!Array.isArray(raw)) return [];
      return raw
        .map(function (entry) {
          // Staršia verzia ukladala holé ID – nech sa nikomu nestratí zoznam.
          var isObject = entry && typeof entry === 'object';
          var id = toId(isObject ? entry.id : entry);
          if (!id) return null;
          var handle = (isObject && entry.handle) || '';
          if (handle) handles[id] = handle;
          return { id: id, handle: handle };
        })
        .filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  function writeLocal(ids) {
    try {
      var entries = ids.map(function (id) {
        return { id: id, handle: handles[id] || '' };
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch (e) {
      // Privátny režim alebo zaplnené úložisko – wishlist prestane prežívať
      // návrat, ale stránka musí fungovať ďalej.
    }
  }

  // Zdroj pravdy pre aktuálnu reláciu – vždy iba ID, handle je len doplnok.
  var items = loggedIn
    ? (cfg.items || [])
        .map(function (e) {
          return toId(e && e.id != null ? e.id : e);
        })
        .filter(Boolean)
    : readLocalEntries().map(function (e) {
        return e.id;
      });

  var listeners = [];

  // Súhlas so strážnym psom. Len pre prihlásených – hosť nemá e-mail v účte.
  var watchdogOn = loggedIn && !!cfg.notify;

  /* Zmena súhlasu má vlastnú udalosť, nie notify() zoznamu – na zoznam je
     naviazané prekreslenie panela, ktorý by sa pri každom prepnutí znova
     načítal a zablikal. */
  function announceWatchdog() {
    document.dispatchEvent(new CustomEvent('dex:watchdog', { detail: { on: watchdogOn } }));
  }

  function notify() {
    var snapshot = items.slice();
    listeners.forEach(function (fn) {
      try {
        fn(snapshot);
      } catch (e) {
        console.error('wishlist listener:', e);
      }
    });
  }

  function request(path, body) {
    return fetch(PROXY + path, {
      method: body ? 'POST' : 'GET',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    }).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.json();
    });
  }

  var api = {
    get items() {
      return items.slice();
    },

    get count() {
      return items.length;
    },

    get loggedIn() {
      return loggedIn;
    },

    get watchdog() {
      return watchdogOn;
    },

    /**
     * Zapne alebo vypne strážneho psa. Optimisticky – prepínač reaguje hneď,
     * pri chybe sa vráti späť, aby zákazník nevidel stav, ktorý sa neuložil.
     */
    setWatchdog: function (enabled) {
      if (!loggedIn) return Promise.reject(new Error('iba pre prihlásených'));
      var before = watchdogOn;
      watchdogOn = !!enabled;
      announceWatchdog();
      return request('/notify', { enabled: watchdogOn })
        .then(function (data) {
          watchdogOn = !!data.notify;
          announceWatchdog();
          return watchdogOn;
        })
        .catch(function (error) {
          watchdogOn = before;
          announceWatchdog();
          throw error;
        });
    },

    has: function (variantId) {
      var id = toId(variantId);
      return !!id && items.indexOf(id) !== -1;
    },

    subscribe: function (fn) {
      listeners.push(fn);
      fn(items.slice());
      return function () {
        listeners = listeners.filter(function (x) {
          return x !== fn;
        });
      };
    },

    /**
     * Prepne variant. Vracia Promise s výsledným stavom.
     *
     * Stav sa mení hneď (optimisticky), aby srdiečko reagovalo okamžite, a pri
     * chybe sa vracia späť – inak by používateľ videl zmenu, ktorá sa neuložila.
     */
    toggle: function (variantId, productHandle) {
      var id = toId(variantId);
      if (!id) return Promise.reject(new Error('neplatné ID variantu'));
      if (productHandle) handles[id] = productHandle;

      var before = items.slice();
      var wasActive = items.indexOf(id) !== -1;

      items = wasActive
        ? items.filter(function (x) {
            return x !== id;
          })
        : items.concat(id);
      notify();

      if (!loggedIn) {
        writeLocal(items);
        return Promise.resolve({ active: !wasActive, count: items.length });
      }

      return request('/toggle', { variantId: id })
        .then(function (data) {
          // Server má posledné slovo – pri dvoch rýchlych klikoch by sa
          // lokálny odhad rozišiel so skutočnosťou.
          items = (data.items || []).map(toId).filter(Boolean);
          notify();
          return { active: !!data.active, count: items.length };
        })
        .catch(function (error) {
          items = before;
          notify();
          throw error;
        });
    },

    /**
     * Položky aj s údajmi na vykreslenie panela.
     * Prihlásený → jedno volanie appky. Hosť → produktové JSON podľa handle,
     * ktorý sme si odložili pri kliknutí na srdiečko.
     */
    detailed: function () {
      if (loggedIn) {
        return request('/list?detailed=1').then(function (data) {
          (data.items || []).forEach(function (item) {
            if (item.handle) handles[item.id] = item.handle;
          });
          // Server má posledné slovo aj o súhlase – Liquid mohol byť z cache.
          if (typeof data.notify === 'boolean' && data.notify !== watchdogOn) {
            watchdogOn = data.notify;
            announceWatchdog();
          }
          return data.items || [];
        });
      }

      var wanted = items.slice();
      var uniqueHandles = [];
      wanted.forEach(function (id) {
        var h = handles[id];
        if (h && uniqueHandles.indexOf(h) === -1) uniqueHandles.push(h);
      });

      // Pri každom handle si pamätáme, či je odpoveď rozhodujúca: produkt sa
      // načítal, alebo neexistuje (404). Výpadok siete rozhodujúci nie je.
      var definitive = {};

      return Promise.all(
        uniqueHandles.map(function (h) {
          return fetch('/products/' + h + '.js')
            .then(function (r) {
              if (r.ok || r.status === 404) definitive[h] = true;
              return r.ok ? r.json() : null;
            })
            .catch(function () {
              return null;
            });
        }),
      ).then(function (products) {
        var byVariant = {};
        products.filter(Boolean).forEach(function (p) {
          (p.variants || []).forEach(function (v) {
            byVariant[String(v.id)] = {
              id: String(v.id),
              title: p.title,
              handle: p.handle,
              variantTitle: v.title,
              price: v.price, // haliere / centy, rovnako ako Admin API
              available: !!v.available,
              image: v.featured_image ? v.featured_image.url : p.featured_image,
              imageAlt: p.title,
            };
          });
        });

        /* Položka, ktorá sa preukázateľne nedá dohľadať – bez handle, zmazaný
           produkt, alebo variant, ktorý v produkte nie je – sa v paneli
           nezobrazí, a teda sa nedá ani odobrať, no počítadlo by ju rátalo
           navždy. Takú vyradíme. Pri výpadku siete nie – zoznam sa nesmie
           stratiť kvôli zlému pripojeniu.
           Vznikla napr. starou chybou, keď srdiečko na karte po prepnutí
           farby ukladalo variant s handle iného produktu. */
        var dead = wanted.filter(function (id) {
          var h = handles[id];
          return !byVariant[id] && (!h || definitive[h]);
        });
        if (dead.length) {
          items = items.filter(function (id) {
            return dead.indexOf(id) === -1;
          });
          writeLocal(items);
          notify();
        }

        // Poradie musí zodpovedať zoznamu, nie poradiu dobehnutých fetchov.
        return wanted
          .map(function (id) {
            return byVariant[id];
          })
          .filter(Boolean);
      });
    },

    /** Prenos hosťovského zoznamu do účtu po prihlásení. */
    mergeLocalIntoAccount: function () {
      var local = readLocalEntries();
      if (!loggedIn || !local.length) return Promise.resolve(null);

      local.forEach(function (e) {
        if (e.handle) handles[e.id] = e.handle;
      });

      return request('/merge', {
        variantIds: local.map(function (e) {
          return e.id;
        }),
      }).then(function (data) {
        items = (data.items || []).map(toId).filter(Boolean);
        // Lokálnu kópiu čistíme až po úspešnom zlúčení, nech sa pri výpadku
        // siete nestratí.
        writeLocal([]);
        notify();
        return data;
      });
    },
  };

  window.DexWishlist = api;

  // Prenos sa spúšťa raz za reláciu, hneď ako sa zistí prihlásený zákazník.
  if (loggedIn) {
    api.mergeLocalIntoAccount().catch(function (error) {
      console.error('wishlist merge:', error);
    });
  }

  /* ---------------- srdiečka ---------------- */

  function paint(button, list) {
    var id = toId(button.dataset.variantId);
    var active = !!id && list.indexOf(id) !== -1;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      active ? 'Odobrať z obľúbených' : 'Pridať do obľúbených',
    );
  }

  api.subscribe(function (list) {
    document.querySelectorAll('[data-dex-wish]').forEach(function (button) {
      paint(button, list);
    });
  });

  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest('[data-dex-wish]');
      if (!button) return;

      // Srdiečko býva vnútri odkazu na produkt – bez tohto by klik navigoval.
      event.preventDefault();
      event.stopPropagation();

      if (button.dataset.busy === '1') return;
      button.dataset.busy = '1';

      api.toggle(button.dataset.variantId, button.dataset.productHandle)
        .catch(function (error) {
          console.error('wishlist toggle:', error);
        })
        .finally(function () {
          delete button.dataset.busy;
        });
    },
    true,
  );

  /**
   * Na produktovej stránke musí srdiečko sledovať prepínač variantov –
   * ukladá sa vždy práve zvolený variant.
   */
  document.addEventListener('variant:change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant || !variant.id) return;

    document
      .querySelectorAll('[data-dex-wish][data-follows-variant]')
      .forEach(function (button) {
        button.dataset.variantId = String(variant.id);
        paint(button, items);
      });
  });

  /* ---------------- zvonček strážneho psa ---------------- */

  /**
   * Zvonček je aktívny, keď je zvolený variant v obľúbených a strážny pes je
   * zapnutý – vtedy zákazníkovi naozaj príde mail.
   */
  function paintBell(bell) {
    var active = loggedIn && watchdogOn && api.has(bell.dataset.variantId);
    var text = active
      ? 'Upozornenie je zapnuté – kliknutím ho zrušíte'
      : 'Upozorniť ma, keď bude na sklade';
    bell.classList.toggle('is-active', active);
    bell.setAttribute('aria-pressed', active ? 'true' : 'false');
    bell.setAttribute('aria-label', text);
    bell.title = text;
  }

  // Poznámka je pod poľom, mimo .select – zvonček na ňu odkazuje cez ID.
  function showBellNote(bell, html) {
    var note = document.getElementById(bell.dataset.noteId);
    if (!note) return;
    note.innerHTML = html || '';
    note.hidden = !html;
  }

  function allBells() {
    return document.querySelectorAll('[data-dex-bell]');
  }

  function paintAllBells() {
    allBells().forEach(paintBell);
  }

  // Zvonček závisí od zoznamu aj od súhlasu – prekresľuje sa pri oboch.
  api.subscribe(paintAllBells);
  document.addEventListener('dex:watchdog', paintAllBells);

  document.addEventListener('click', function (event) {
    var bell = event.target.closest('[data-dex-bell]');
    if (!bell) return;
    event.preventDefault();
    if (bell.dataset.busy === '1') return;
    var btn = bell;

    // Upozornenie ide na e-mail z účtu – hosťovi ho nemáme kam poslať.
    if (!loggedIn) {
      showBellNote(
        bell,
        'Upozornenie posielame na e-mail z vášho účtu. ' +
          '<a href="' + (cfg.loginUrl || '/account/login') + '">Prihláste sa</a> ' +
          'a kliknite znova.',
      );
      return;
    }

    var id = bell.dataset.variantId;
    var active = watchdogOn && api.has(id);
    btn.dataset.busy = '1';
    showBellNote(bell, '');

    var work;
    if (active) {
      // Zrušenie: položka zo zoznamu preč. Strážny pes ostáva zapnutý pre
      // ostatné obľúbené – vypína sa v paneli.
      work = api.toggle(id, bell.dataset.productHandle);
    } else {
      work = (api.has(id) ? Promise.resolve() : api.toggle(id, bell.dataset.productHandle)).then(
        function () {
          return watchdogOn ? null : api.setWatchdog(true);
        },
      );
    }

    work
      .then(function () {
        if (!active) {
          showBellNote(
            bell,
            'Upozorníme vás e-mailom, keď bude na sklade. Položku sme uložili aj do obľúbených.',
          );
        }
      })
      .catch(function (error) {
        console.error('watchdog bell:', error);
        showBellNote(bell, 'Nepodarilo sa to uložiť. Skúste to znova.');
      })
      .finally(function () {
        delete btn.dataset.busy;
        paintBell(bell);
      });
  });

  /**
   * Zvonček sleduje prepínač variantov: ukáže sa pri nedostupnom variante
   * a pri dostupnom sa skryje.
   *
   * ponytail: kombinácia možností, ktorá ako variant neexistuje, udalosť
   * nevyvolá – zvonček vtedy ostane pri poslednom platnom variante. Pri
   * produktoch SCANquiltu s jednou možnosťou (veľkosť) nenastáva.
   */
  document.addEventListener('variant:change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant || !variant.id) return;

    // Len zvonček v poli – zvončeky v zozname variantov patria každý svojmu
    // variantu a prepísať sa nesmú.
    document.querySelectorAll('[data-dex-bell][data-follows-variant]').forEach(function (bell) {
      bell.dataset.variantId = String(variant.id);
      bell.hidden = !!variant.available;
      // Pole potrebuje miesto pre zvonček, len keď je viditeľný.
      var select = bell.closest('.select');
      if (select) select.classList.toggle('dex-select--bell', !variant.available);
      showBellNote(bell, '');
      paintBell(bell);
    });
  });

  /** Nové srdiečka a zvončeky po AJAX prekreslení (karty, quick view, zoznam variantov). */
  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      for (var j = 0; j < mutations[i].addedNodes.length; j++) {
        var node = mutations[i].addedNodes[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('[data-dex-wish]')) paint(node, items);
        if (node.matches && node.matches('[data-dex-bell]')) paintBell(node);
        if (node.querySelectorAll) {
          node.querySelectorAll('[data-dex-wish]').forEach(function (b) {
            paint(b, items);
          });
          node.querySelectorAll('[data-dex-bell]').forEach(paintBell);
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
